import { useMemo, useState } from 'react';
import { Button, Table, Dialog, ConfirmDialog, Input, Switch, Badge, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Pencil, Merge, Trash2 } from 'lucide-react';
import { PageHeader, QueryState, EmptyState } from '@/components';
import { errorText } from '@/lib/format';
import { useCan } from '@rbac/useCan';
import Can from '@rbac/Can';
import { useProductTypes } from '@/lib/productTypes';
import { useCrudList, useCrudWrite } from '../hooks';
import { brandTypes } from '../lib/brands';
import BrandSelect from '../components/BrandSelect';

/**
 * The store's brand list (D39), the Products "Brands" tab. Add, rename, turn off,
 * merge duplicates into one, and delete a brand no model uses. Model forms pick
 * from this list (BrandSelect); a rename shows on every model of the brand.
 */
export default function Brands({ embedded = false }) {
  const can = useCan();
  const { toast } = useToast();
  const registry = useProductTypes();
  const list = useCrudList('Brand', { orderBy: { column: 'name', direction: 'asc' }, limit: 1000 });
  const models = useCrudList('ProductModel', { select: ['id', 'brand_id'], limit: 5000 });
  const write = useCrudWrite();
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [merging, setMerging] = useState(null); // { brand, into }
  const [deleting, setDeleting] = useState(null);
  const [formError, setFormError] = useState('');

  const usage = useMemo(() => {
    const m = new Map();
    for (const r of models.data || []) if (r.brand_id != null) m.set(String(r.brand_id), (m.get(String(r.brand_id)) || 0) + 1);
    return m;
  }, [models.data]);
  const used = (b) => usage.get(String(b.id)) || 0;
  const reload = () => { list.reload(); models.reload(); };
  const run = async (fn, done) => {
    setFormError('');
    try {
      await fn();
      done();
      reload();
    } catch (ex) {
      setFormError(errorText(ex));
    }
  };

  const add = () => run(() => write.create('Brand', { name: newName }), () => { toast.success('Brand added', newName.trim()); setNewName(''); });
  const rename = () => run(() => write.update('Brand', renaming.id, { name: renaming.name }), () => { toast.success('Brand renamed', renaming.name.trim()); setRenaming(null); });
  const toggle = (b, on) => run(() => write.update('Brand', b.id, { is_active: on ? 1 : 0 }), () => toast.success(on ? 'Brand turned on' : 'Brand turned off', b.name));
  const merge = () => run(() => write.run({ model: 'Brand', action: 'merge', data: { from_id: merging.brand.id, into_id: Number(merging.into) } }), () => { toast.success('Brands merged', `${merging.brand.name} → ${merging.intoName}`); setMerging(null); });
  const remove = () => run(() => write.run({ model: 'Brand', action: 'delete', where: { id: deleting.id } }), () => { toast.success('Brand deleted', deleting.name); setDeleting(null); });

  const typeLabels = (b) => brandTypes(b).map((k) => (registry.byKey[k] || {}).label || k);
  const actions = [
    can('catalog', 'update') && { name: 'rename', label: 'Rename', icon: <Pencil size={14} />, inline: true, onClick: ({ row }) => { setFormError(''); setRenaming({ id: row.id, name: row.name }); } },
    can('catalog', 'update') && { name: 'merge', label: 'Merge into…', icon: <Merge size={14} />, onClick: ({ row }) => { setFormError(''); setMerging({ brand: row, into: '', intoName: '' }); } },
    can('catalog', 'delete') && { name: 'delete', label: 'Delete', icon: <Trash2 size={14} />, onClick: ({ row }) => { setFormError(''); setDeleting(row); } },
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <PageHeader title={embedded ? undefined : 'Brands'} subtitle="Your brand list. Models pick a brand from it, so filters and reports never split “Dell” and “DELL”." />
      <Can module="catalog" action="create">
        <form className="flex max-w-md gap-2" onSubmit={(e) => { e.preventDefault(); if (newName.trim()) add(); }}>
          <Input aria-label="New brand name" placeholder="Add a brand, e.g. Xiaomi" value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={120} />
          <Button type="submit" leftIcon={<Plus size={16} />} disabled={!newName.trim()} loading={write.loading && !renaming && !merging && !deleting}>Add</Button>
        </form>
      </Can>
      {formError && !renaming && !merging && !deleting ? <Alert variant="error" title="Not saved">{formError}</Alert> : null}

      <QueryState query={list}>
        {(rows) =>
          rows.length ? (
            <Table
              data={rows}
              columns={[
                { key: 'name', label: 'Brand', render: (b) => <span className="font-medium">{b.name}</span> },
                { key: 'types', label: 'Shown first for', render: (b) => (typeLabels(b).length ? <div className="flex flex-wrap gap-1">{typeLabels(b).map((l) => <Badge key={l} variant="secondary" size="sm">{l}</Badge>)}</div> : '—'), hideOnMobile: true },
                { key: 'models', label: 'Models', render: (b) => used(b) },
                {
                  key: 'is_active', label: 'In forms',
                  render: (b) => <Switch checked={Number(b.is_active) !== 0} onChange={(on) => toggle(b, on)} ariaLabel={`Offer ${b.name} in forms`} disabled={!can('catalog', 'update')} size="sm" />,
                },
              ]}
              actions={actions}
              replaceDefaultActions
              withAction={actions.length > 0}
              showSerial={false}
              showColumnPicker={false}
              globalSearch
              pagination
              pageSize={25}
            />
          ) : (
            <EmptyState title="No brands yet">Add the brands you sell, or type a new one while adding a product.</EmptyState>
          )
        }
      </QueryState>

      <Dialog isOpen={!!renaming} onClose={() => setRenaming(null)} title="Rename brand" size="sm">
        {renaming ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); rename(); }}>
            <Input label="Name" required value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} maxLength={120} error={formError || undefined} />
            <p className="text-xs opacity-70">Every model of this brand shows the new name.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRenaming(null)}>Cancel</Button>
              <Button type="submit" loading={write.loading} disabled={!renaming.name.trim()}>Save</Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      <Dialog isOpen={!!merging} onClose={() => setMerging(null)} title={merging ? `Merge ${merging.brand.name} into…` : 'Merge'} size="sm">
        {merging ? (
          <div className="space-y-3">
            <BrandSelect label="Keep this brand" value={merging.into} excludeId={merging.brand.id} onChange={(id, name) => setMerging({ ...merging, into: id, intoName: name })} helperText={`${used(merging.brand)} model(s) move to it, and ${merging.brand.name} is deleted.`} />
            {formError ? <Alert variant="error" title="Not merged">{formError}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMerging(null)}>Cancel</Button>
              <Button onClick={merge} loading={write.loading} disabled={!merging.into}>Merge</Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      <ConfirmDialog
        isOpen={!!deleting}
        title={deleting ? `Delete ${deleting.name}?` : 'Delete brand?'}
        description={deleting && used(deleting) ? `${used(deleting)} model(s) use this brand. Turn it off or merge it into another brand instead.` : 'It is removed from your brand list.'}
        confirmLabel="Delete"
        loading={write.loading}
        onCancel={() => setDeleting(null)}
        onConfirm={remove}
      />
      {deleting && formError ? <Alert variant="error" title="Not deleted">{formError}</Alert> : null}
    </div>
  );
}
